import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
  untracked,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { userDtos } from '@vectronic/openApi-customer';
import {
  EventFilter,
  EventHelp,
  EventRequestDto,
  EventType,
  FilterItemWithLabel,
  MobileService,
  SessionStorageService,
  eventFilterToRequest,
} from '@vectronic/util';
import { debounceTime } from 'rxjs';
import { UtilService } from '../../services/util.service';
import { CoordinatesFilterComponent } from '../coordinates-filter/coordinates-filter.component';
import { InputFilterWithHeaderComponent } from '../input-filter-with-header/input-filter-with-header.component';
import { InputSearchFilterComponent } from '../input-search-filter/input-search-filter.component';
import { InvalidPositionsFilterComponent } from '../invalid-positions-filter/invalid-positions-filter.component';
import { LastXRecordsFilterComponent } from '../last-x-records-filter/last-x-records-filter.component';
import { MapFixTypeFilterComponent } from '../map-fix-type-filter/map-fix-type-filter.component';
import { MultiSelectChipFilterComponent } from '../multi-select-chip-filter/multi-select-chip-filter.component';
import { MultiSelectFilterComponent } from '../multi-select-dropdown/multi-select-filter.component';
import { ShowSystemLogFilterComponent } from '../show-system-log-filter/show-system-log-filter.component';
import { SingleSelectFilterComponent } from '../single-select-dropdown/single-select-filter.component';
import { SyncedDataFilterComponent } from '../synced-data-filter/synced-data-filter.component';
import { UtcFilterComponent } from '../utc-filter/utc-filter.component';
import { VectronicLastReceptionComponent } from '../vectronic-last-reception/vectronic-last-reception.component';
import { DeployedOnlyComponent } from './deployed-only/deployed-only.component';
import { DownloadTimestampService } from './downloadTs.service';
import { FilterTitles, filterList } from './filter-options-helper';

@Component({
  selector: 'vectronic-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    InvalidPositionsFilterComponent,
    SyncedDataFilterComponent,
    ShowSystemLogFilterComponent,
    UtcFilterComponent,
    LastXRecordsFilterComponent,
    MapFixTypeFilterComponent,
    CoordinatesFilterComponent,
    MultiSelectFilterComponent,
    SingleSelectFilterComponent,
    InputSearchFilterComponent,
    DeployedOnlyComponent,
    VectronicLastReceptionComponent,
    MatDividerModule,
    InputFilterWithHeaderComponent,
    MultiSelectChipFilterComponent,
  ],
})
export class FiltersComponent implements OnInit {
  groupsList = input(null, {
    transform: (value: string[] | null) => {
      if (!value) return null;
      return [
        { item: 'Without group', label: 'Without group' },
        ...value.map((v) => ({ item: v, label: v })),
      ];
    },
  });

  isDashboardPage = input<boolean>();
  userPreferences = input<userDtos.UserPreferences>();
  filtersToDisplay = input<string[]>();
  pageName = input<string>();
  initialLastXRecordsValue = input<number>();

  pageComponent = input<any>();

  isLocalTimeFilterVisible = input<boolean>(true);
  itemList = input<FilterItemWithLabel<string>[]>([]);
  protected readonly InputFilterWithHeaderComponent = InputFilterWithHeaderComponent;
  /*
   description filter will use extendedMessageType, which will have impact on all event type filtering.
   however, this filter should have unique behaviour for VIT and SEP only. this is why the parent can
   decide, if the description filter should be reset whenever the filtersToDisplay changes.
   */
  resetDescriptionFilterWhenFiltersToDisplayChange = input(false);

  private isAnimalPage = computed(() => this.pageName() === 'animals-page');
  private isCollarPage = computed(() => this.pageName() === 'collars-page');
  private isPositionPage = computed(() => this.pageName() === 'positions');
  private isEventPage = computed(() => EventHelp.all().includes(this.pageName() as EventType));

  filtersForm: FormGroup;
  formGroupReady = false;
  FilterTitles = FilterTitles;
  filterValuesSaved = output<EventRequestDto>();
  isDesktop = Boolean(window.desktop || window.adminDesktop);

  private fb = inject(FormBuilder);
  private sessionStorage = inject(SessionStorageService);
  private downloadTsService = inject(DownloadTimestampService);
  private mobileService = inject(MobileService);
  private utilService = inject(UtilService);
  FilterList = filterList;

  constructor() {
    effect(() => {
      const pageName = this.pageName();
      if (!pageName || !this.filtersForm) return;

      const downloadHours = this.downloadTsService.get(pageName);
      if (downloadHours) {
        this.filtersForm.patchValue({
          dtsFilterEnabled: true,
          dtsMode: 'recent',
          dtsRecentUnit: 'h',
          dtsRecentValue: downloadHours,
          onlyLocalData: true,
          showSystemLog: true,
        });
        untracked(() => this.apply(false));
      }
    });
  }

  ngOnInit(): void {
    this.onInitialization();
    // For animals-page and collars-page, show buttons and only apply on click
    // For other pages, auto-apply on form changes
    if (this.isCollarOrAnimalPage()) {
      this.filtersForm.valueChanges.pipe(debounceTime(300)).subscribe(() => {
        this.apply();
      });
    }
  }

  onApply() {
    this.apply();
  }

  onReset() {
    this.filtersForm.reset();
    this.apply();
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  onInitialization() {
    this.isDesktop = (window.desktop ?? window.adminDesktop) !== undefined;
    const utcGroup = {
      utcFilterEnabled: false,
      utcMode: undefined,
      utcFrom: null,
      utcTo: null,
      lmtFrom: null,
      lmtTo: null,
      utcRecentValue: '',
      utcRecentUnit: '',
    };
    const sctsGroup = {
      sctsFilterEnabled: false,
      sctsMode: undefined,
      sctsFrom: null,
      sctsTo: null,
      sctsLmtFrom: null,
      sctsLmtTo: null,
      sctsRecentValue: '',
      sctsRecentUnit: '',
    };
    const dtsGroup = {
      dtsFilterEnabled: false,
      dtsMode: undefined,
      dtsFrom: null,
      dtsTo: null,
      dtsLmtFrom: null,
      dtsLmtTo: null,
      dtsRecentValue: '',
      dtsRecentUnit: '',
    };
    const lastReceptionGroup = {
      lastReceptionFilterEnabled: false,
      lastReceptionDate: '',
      lastReceptionUnit: '',
    };
    this.filtersForm = this.fb.group({
      lastXRecordsFilterEnabled: false,
      lastXRecords: undefined,
      ...utcGroup,
      ...sctsGroup,
      ...dtsGroup,
      ...lastReceptionGroup,
      deployedOnlyFilterEnabled: false,
      originFilterEnabled: false,
      origin: [],
      fixTypeFilterEnabled: false,
      fixType: [],
      mapFixTypeFilterEnabled: true,
      mapFixType: 'HIDE_INVALID',
      vfEntered: undefined,
      vfEnteredFilterEnabled: false,
      collarAttachedFilterEnabled: false,
      collarAttached: undefined,
      speciesFilterEnabled: false,
      species: [],
      sexFilterEnabled: false,
      sex: [],
      mortalityStatusFilterEnabled: false,
      mortalityStatus: [],
      mortalityTypeFilterEnabled: false,
      mortalityType: [],
      descriptionFilterEnabled: false,
      description: [],
      groupsFilterEnabled: false,
      groups: [],
      animalNameFilterEnabled: false,
      animalName: '',
      deviceIdsFilterEnabled: false,
      deviceIds: [],
      comTypeFilterEnabled: false,
      comType: [],
      deviceTypeFilterEnabled: false,
      deviceType: [],
      deviceIdFilterEnabled: false,
      deviceId: '',
      networkStatusFilterEnabled: false,
      networkStatus: [],
      hideInvalidPositions: false,
      onlyLocalData: false,
      showSystemLog: Boolean(window.adminDesktop),
      coordinatesFilterEnabled: false,
      minLatitude: '',
      maxLatitude: '',
      minLongitude: '',
      maxLongitude: '',
      minAltitude: '',
      maxAltitude: '',
      recentUnit: '',
    } as EventFilter);

    if (this.isCollarOrAnimalPage() && !this.mobileService.isOn()) {
      this.formGroupReady = true;
      return;
    }

    const localStorageFilters = this.sessionStorage.get('filters-form-' + this.pageName());

    // Apply userPreferences if no filters in sessionStorage
    if (localStorageFilters === null && this.userPreferences()) {
      const prefs = this.userPreferences();
      const preferencePatch: any = {};

      if (prefs) {
        // Handle dashboard page preferences
        if (this.isDashboardPage() && this.pageName() === 'dashboard') {
          if (prefs.acquisitionTimeAfter) {
            preferencePatch['utcFilterEnabled'] = true;
            preferencePatch['utcMode'] = 'time-span';
            preferencePatch['utcFrom'] = prefs.acquisitionTimeAfter;
            preferencePatch['utcTo'] = prefs.acquisitionTimeBefore ?? new Date().toISOString();
          } else if (prefs.amount && prefs.unit && prefs.unit !== 'p') {
            preferencePatch['utcFilterEnabled'] = true;
            preferencePatch['utcMode'] = 'recent';
            preferencePatch['utcRecentValue'] = prefs.amount;
            preferencePatch['utcRecentUnit'] = prefs.unit;
          } else if (prefs.amount) {
            preferencePatch['lastXRecordsFilterEnabled'] = true;
            preferencePatch['lastXRecords'] = prefs.amount;
          }
        }
        // Handle map page preferences
        else if (this.pageName() === 'map') {
          if (prefs.mapAcquisitionTimeFrom) {
            preferencePatch['utcFilterEnabled'] = true;
            preferencePatch['utcMode'] = 'time-span';
            preferencePatch['utcFrom'] = prefs.mapAcquisitionTimeFrom;
            preferencePatch['utcTo'] = prefs.mapAcquisitionTimeTo ?? new Date().toISOString();
          } else if (
            prefs.mapMostRecentAmount &&
            prefs.mapMostRecentUnit &&
            prefs.mapMostRecentUnit !== 'p'
          ) {
            preferencePatch['utcFilterEnabled'] = true;
            preferencePatch['utcMode'] = 'recent';
            preferencePatch['utcRecentValue'] = prefs.mapMostRecentAmount;
            preferencePatch['utcRecentUnit'] = prefs.mapMostRecentUnit;
          } else if (prefs.mapMostRecentAmount) {
            preferencePatch['lastXRecordsFilterEnabled'] = true;
            preferencePatch['lastXRecords'] = prefs.mapMostRecentAmount;
          }
        }
      }

      this.filtersForm.patchValue(preferencePatch);
      this.apply();
    } else if (localStorageFilters != null) {
      try {
        const parsedFilters = JSON.parse(localStorageFilters);
        const filteredFilters = {};

        for (const key in parsedFilters) {
          if (
            Object.prototype.hasOwnProperty.call(parsedFilters, key) &&
            parsedFilters[key] !== null
          ) {
            filteredFilters[key] = parsedFilters[key];
          }
        }

        if (this.initialLastXRecordsValue() !== undefined) {
          this.filtersForm.get('lastXRecordsFilterEnabled').setValue(true);
        }
        this.filtersForm.patchValue(filteredFilters);
        this.apply();
      } catch (e) {
        console.log(e);
      }
    } else if (this.initialLastXRecordsValue()) {
      this.filtersForm.get('lastXRecordsFilterEnabled').setValue(true);
      this.filtersForm.get('lastXRecords').setValue(this.initialLastXRecordsValue().toString());
      this.apply();
    }

    this.formGroupReady = true;
  }

  apply(doSave = true) {
    if ((doSave && !this.isCollarOrAnimalPage()) || this.mobileService.isOn()) {
      this.sessionStorage.set('filters-form-' + this.pageName(), this.filtersForm.value);
    }
    const request = eventFilterToRequest(this.filtersForm.value, {
      includeFixTypeMode: this.filtersToDisplay().includes('mapFixType'),
    });

    this.filterValuesSaved.emit(request);
    this.utilService.activeFiltersCount.set(this.getEnabledFilterCount(request));
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getEnabledFilterCount(request: ReturnType<typeof eventFilterToRequest>): number {
    let count = 0;
    if (request.fixTypeMode && request.fixTypeMode !== 'HIDE_INVALID') count++;
    if (this.isCollarPage() || this.isAnimalPage()) {
      if (request.deviceIds) count++;
      if (request.networkStatus) count++;
      if (request.lastReceptionDate) count++;
      if (request.mortalityStatusSingle) count++;
      if (request.collarAttached) count++;
      if (request.groups) count++;
      if (request.comType) count++;
      if (request.deviceType) count++;
      if (request.species) count++;
      if (request.sex) count++;
      if (request.deployedOnly) count++;
    } else if (this.isPositionPage() || this.isEventPage()) {
      if (this.isDesktop) {
        if (request.onlyLocal) count++;
        if (request.afterDTS || request.beforeDTS) count++;
        if (this.pageName() === EventType.COLLAR_LOG && !request.doHideSystemLogs) count++;
      }
      if (request.afterUTC || request.beforeUTC) count++;
      if (request.afterSCTS || request.beforeSCTS) count++;
      if (request.deployedOnly) count++;
      if (request.extendedMessageTypes) count++;
      if (request.lastX) count++;
      if (request.originCodes) count++;
      if (request.vfEntered) count++;
      if (request.statusSearch) count++;
      if (this.isPositionPage()) {
        if (request.mortalityStatusCodes) count++;
        if (!request.showInvalidPositions) count++;
        if (request.fixTypeSearch) count++;
        if (
          request.maxAltitude ||
          request.maxLatitude ||
          request.maxLongitude ||
          request.minAltitude ||
          request.minLatitude ||
          request.minLongitude
        )
          count++;
      }
    }

    return count;
  }

  isCollarOrAnimalPage() {
    return this.pageName() === 'animals-page' || this.pageName() === 'collars-page';
  }
}
